import { Graph, alg } from 'graphlib';
import IntervalTree from '@flatten-js/interval-tree'

export const PARTIAL_COVERAGE_METHODS = ['PARTIALX', 'PARTIALP', 'PARTIAL_CONTIG_ENDX', 'PARTIAL_CONTIG_ENDP', 'INTERNAL_STOP'];

export const getGeneCoverage = (gene) => {
  const methods = String(gene?.method || '')
    .split(',')
    .map(method => method.trim())
    .filter(Boolean);

  if (methods.length === 0 || methods.some(method => method.toUpperCase() === 'UNKNOWN')) {
    return 'Unknown';
  }

  if (methods.some(method => PARTIAL_COVERAGE_METHODS.includes(method))) {
    return 'Partial';
  }

  return 'High';
};

export const geneHasPartialCoverage = (gene) => getGeneCoverage(gene) === 'Partial';

/**
 * Determines if two intervals have an overlap greater than a given threshold.
 *
 * @param {number} start1 - The start index of the first interval.
 * @param {number} stop1 - The stop index of the first interval.
 * @param {number} start2 - The start index of the second interval.
 * @param {number} stop2 - The stop index of the second interval.
 * @param {number} threshold - The overlap threshold (e.g., 0.8 for 80%).
 * 
 * @returns {boolean} True if the intervals overlap by more than the threshold, false otherwise.
 */
function hasSignificantOverlap(start1, stop1, start2, stop2, threshold) {
  const intersection = Math.max(0, Math.min(stop1, stop2) - Math.max(start1, start2));
  const length1 = stop1 - start1;
  const length2 = stop2 - start2;
  return (length1 > 0 && intersection / length1 > threshold) ||
         (length2 > 0 && intersection / length2 > threshold);
}

/**
 * Identifies overlapping resistance genes where each gene overlaps
 * with at least one other gene by more than the provided threshold.
 * 
 * Uses an interval tree to avoid comparing resistance genes that do not overlap.
 *
 * @param {Array} genes - An array of gene objects, each with keys { id, contig_id, strand, start, stop }.
 * @param {number} [threshold=0.8] - The overlap threshold (e.g., 0.8 for 80%).
 * @returns {Array} An array of clusters; each cluster is an array of gene ids.
 */
export function identifyOverlappingResistanceGenes(genes, threshold = 0.8) {
  const graph = new Graph({ directed: false });
  genes.forEach(({ id }) => graph.setNode(id));

  const tree = new IntervalTree();
  genes.forEach(({ id, contig_id, strand, start, stop }) => {
    // Keep strand in the candidate payload because overlap clustering intentionally
    // requires matching contig and strand before checking coordinate overlap.
    tree.insert([start, stop], { id, contig_id, strand, start, stop });
  });

  const initialAcc = { processedPairs: new Set(), graph };
  genes.reduce((acc, { id, contig_id, strand, start, stop }) => {
    const candidates = tree.search([start, stop]);
    candidates.forEach(({ id: candidateId, contig_id: candidateContigID, strand: candidateStrand, start: candidateStart, stop: candidateStop }) => {
      if ((id === candidateId) || (contig_id !== candidateContigID) || (strand !== candidateStrand)) return;

      const pairKey = id < candidateId ? `${id}-${candidateId}` : `${candidateId}-${id}`;
      if (acc.processedPairs.has(pairKey)) return;

      acc.processedPairs.add(pairKey);

      if (hasSignificantOverlap(start, stop, candidateStart, candidateStop, threshold)) {
        acc.graph.setEdge(id, candidateId);
      }
    });
    return acc;
  }, initialAcc);

  return alg.components(graph);
}

/**
 * Builds the display/export rows used by the Complete Resistance Marker Profile.
 *
 * The API returns raw resistance-gene hits per identified species. The UI needs a
 * display-ready marker list that mirrors existing Pathosight interpretation rules:
 * keep only positive species and AMR hits, deduplicate repeated gene symbols,
 * collapse strongly overlapping hits, apply KgAST quality visibility rules, flag
 * markers that appear assignable to more than one visible organism, then shape the
 * result for the DataGrid and dataset-level CSV export.
 *
 * @param {Object} params
 * @param {Array} params.identifiedSpecies - Identified species records for one sample.
 * @param {Array} params.resistanceGenes - Resistance-gene records for those species.
 * @param {boolean} params.isIllumina - Whether sample/dataset sequencing is Illumina.
 * @returns {{ rows: Array, showKgASTQualityWarning: boolean, hasFilteredResistanceGenes: boolean }}
 */
export function buildResistanceGeneRows({ identifiedSpecies = [], resistanceGenes = [], isIllumina = false }) {
  // Only positive species participate in marker display. Polymicrobial and
  // Illumina samples use stricter visibility rules for species failing KgAST QC.
  const positiveSpecies = (identifiedSpecies || [])
    .filter(species => species?.attributes?.species_call_positive);
  const isPolymicrobial = positiveSpecies.length > 1;
  const filterOutResGenesWithQualityLimitations = isPolymicrobial || isIllumina;

  // Build all AMR markers per species before QC visibility filtering. Duplicate
  // marker flagging depends on the full available marker set, not only visible rows.
  const allAvailableResistanceGenes = positiveSpecies
    .map(species => {
      const filteredGenes = (resistanceGenes || [])
        .filter(gene => gene?.attributes?.identified_species_id === species.id)
        .filter(gene => gene?.attributes?.element_type?.toUpperCase() === 'AMR');

      // Preserve existing table behavior: if the same gene_symbol appears more
      // than once for a species, the later record wins before overlap clustering.
      const uniqueGenes = filteredGenes.reduce((acc, gene) => {
        const { id, attributes } = gene;
        acc[attributes.gene_symbol] = {
          id,
          contig_id: attributes.contig_id,
          strand: attributes.strand,
          start: attributes.start,
          stop: attributes.stop,
          gene_symbol: attributes.gene_symbol,
          drug_class: attributes.drug_class || 'Unknown',
          method: attributes.method,
          gene_family: attributes.gene_family
        };
        return acc;
      }, {});

      const genesById = Object.values(uniqueGenes).reduce((acc, gene) => {
        acc[gene.id] = gene;
        return acc;
      }, {});

      const geneClusters = identifyOverlappingResistanceGenes(Object.values(genesById));

      // Collapse overlapping hits into the same display row used by the existing
      // marker profile table. Multi-family clusters get a "Gene similar to ..."
      // label, while one-family clusters use that single family/symbol directly.
      const uniqueGenesWithOverlaps = geneClusters.map(cluster => {
        if (cluster.length === 1) {
          const gene = genesById[cluster[0]];
          return {
            contig_id: gene.contig_id,
            start: gene.start,
            stop: gene.stop,
            gene_symbol: gene.gene_symbol,
            drug_class: gene.drug_class,
            method: gene.method
          };
        }

        const geneFamilies = new Set(cluster.map(id => genesById[id].gene_family || genesById[id].gene_symbol));
        const geneFamilyLabels = Array.from(geneFamilies);
        const uniqueDrugClasses = new Set(cluster.flatMap(id => genesById[id].drug_class.split('/')));
        const uniqueMethods = new Set(cluster.map(id => genesById[id].method));

        return {
          gene_symbol: geneFamilyLabels.length > 1 ? `Gene similar to ${geneFamilyLabels.join(' or ')}` : geneFamilyLabels[0],
          drug_class: Array.from(uniqueDrugClasses).join('/'),
          method: Array.from(uniqueMethods).join(', '),
        };
      });

      return { species_name: species.attributes.species, genes: uniqueGenesWithOverlaps };
    })
    .reduce((acc, { species_name, genes }) => {
      if (genes.length) acc[species_name] = genes;
      return acc;
    }, {});

  // Species with marker rows but failed KgAST QC should trigger warning UI. For
  // Illumina or polymicrobial samples, those species' marker rows are hidden.
  const speciesFailingKgASTQc = positiveSpecies
    .filter(species => (species?.attributes?.reasons_for_qc_failure || []).length > 0)
    .map(species => species.attributes.species);
  const speciesWithResistanceGenesFailingQc = new Set(
    Object.keys(allAvailableResistanceGenes)
      .filter(species => speciesFailingKgASTQc.includes(species))
  );

  const visibleResistanceGenes = !filterOutResGenesWithQualityLimitations ? allAvailableResistanceGenes :
    Object.entries(allAvailableResistanceGenes)
      .reduce((acc, [speciesName, genes]) => {
        if (!speciesWithResistanceGenesFailingQc.has(speciesName)) acc[speciesName] = genes;
        return acc;
      }, {});

  // Flag markers that appear at the same contig/gene/start/stop in multiple
  // species. The UI renders these with an asterisk and the dataset CSV exposes
  // the same information as unclassified_duplicate_marker.
  const identicalMarkersMap = new Map();
  Object.entries(allAvailableResistanceGenes).forEach(([, genes]) => {
    genes.forEach(gene => {
      if (gene?.contig_id && gene.gene_symbol && gene?.start && gene?.stop) {
        const key = `${gene.contig_id}-${gene.gene_symbol}-${gene.start}-${gene.stop}`;
        identicalMarkersMap.set(key, (identicalMarkersMap.get(key) || 0) + 1);
      }
    });
  });

  // Final rows are sorted to match the profile table: organism, drug class, marker.
  const rows = Object.entries(visibleResistanceGenes)
    .sort(([speciesNameA], [speciesNameB]) => speciesNameA.localeCompare(speciesNameB))
    .flatMap(
      ([speciesName, genes]) =>
        genes
          .sort((a, b) => {
            const drugClassComparison = a.drug_class.localeCompare(b.drug_class);
            return drugClassComparison !== 0 ? drugClassComparison : a.gene_symbol.localeCompare(b.gene_symbol);
          })
          .map((gene, index) => {
            const key = `${gene?.contig_id}-${gene.gene_symbol}-${gene?.start}-${gene?.stop}`;
            return {
              id: `${speciesName}-${index}`,
              organism: speciesName,
              gene: gene.gene_symbol,
              drugClass: gene.drug_class,
              coverage: getGeneCoverage(gene),
              unclassifiedDuplicateMarker: (identicalMarkersMap.get(key) || 0) > 1
            };
          })
    );

  return {
    rows,
    showKgASTQualityWarning: speciesWithResistanceGenesFailingQc.size > 0,
    hasFilteredResistanceGenes: filterOutResGenesWithQualityLimitations && speciesWithResistanceGenesFailingQc.size > 0,
  };
}
